import React, { createContext, useContext } from 'react';
import {
  useFeatureContext,
  FeatureFlag,
} from 'context/FeaturesContext/FeaturesContext';

interface FeatureProps {
  readonly children: React.ReactNode;
  readonly flag: FeatureFlag;
}

/**
 * Renders children when the specified feature flag is enabled
 */
function Feature({ flag, children }: FeatureProps) {
  const isFeatureEnabled = useFeatureContext(flag);
  return isFeatureEnabled ? <>{children}</> : null;
}

interface FeatureSwitchProps {
  readonly children: React.ReactNode;
  readonly flag: FeatureFlag;
}

const FeatureSwitchContext = createContext(false);

/**
 * Renders children for either/both states of a feature flag - via compound components.
 */
export function FeatureSwitch({ children, flag }: FeatureSwitchProps) {
  const isFeatureEnabled = useFeatureContext(flag);
  return (
    <FeatureSwitchContext.Provider value={isFeatureEnabled}>
      {children}
    </FeatureSwitchContext.Provider>
  );
}

function On({ children }: { children: React.ReactNode }) {
  const isEnabled = useContext(FeatureSwitchContext);
  return isEnabled ? <>{children}</> : null;
}

function Off({ children }: { children: React.ReactNode }) {
  const isEnabled = useContext(FeatureSwitchContext);
  return isEnabled ? null : <>{children}</>;
}

FeatureSwitch.On = On;
FeatureSwitch.Off = Off;

export default Feature;
